import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  HandoffCommunicationType,
  APIContact,
  ContactGroup,
  APIHandoffRule,
} from '@super-software-inc/foundation'
import { Formik, Form, Field, FormikProps } from 'formik'
import * as Yup from 'yup'
import {
  FlexRow,
  Label,
  PrimaryButton,
  TextInput,
  Select,
} from 'components/lib'
import { toastError, toastSuccess } from 'components/lib/Toast'
import FixedBottomButtons from 'components/lib/Forms/FixedBottomButtons'
import { MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md'
import { EditorRef } from 'components/app/Editor/types'
import ScheduleField from './ScheduleField'
import {
  getDefaultSchedule,
  transformScheduleForBackend,
  transformBackendScheduleToUI,
  normalizeBackendSchedule,
  getScheduleTypeFromBackendRule,
  getScheduleForDisplay,
  scheduleSummary,
} from '../../../utils/schedule/transformers'
import { ScheduleData, ScheduleType } from '../../../utils/schedule/types'

import {
  allHandoffRulesAtom,
  authenticatedUserAtom,
  handoffRuleFormAtom,
} from '../../../state/atoms'
import {
  createHandoffRule,
  updateHandoffRule,
} from '../../../api/assistant/handoffRule'
import { getContacts } from '../../../api/contacts/getContacts'
import { findCompanyById } from '../../../api/companies'

import PropertySelector from '../Tasks/PropertySelector'
import Editor from '../Editor/Editor'

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  description: Yup.string().required('Prompt is required'),
  communicationType: Yup.array().required('Communication type is required'),
  associationIds: Yup.array().of(Yup.string()).optional(),
  timeRestrictions: Yup.object().shape({
    applyDuring: Yup.string().optional(),
    schedule: Yup.mixed().optional(), // Simplified - just allow any schedule data
  }),
  scheduleType: Yup.string().required(),
})

export interface HandoffRuleFormValues {
  title: string
  description: string
  associationIds: string[]
  communicationType: HandoffCommunicationType[]
  timeRestrictions: {
    applyDuring?: string
    schedule?: ScheduleData
  }
  scheduleType: ScheduleType
}

interface HandoffRuleFormState {
  handoffRule: Partial<APIHandoffRule>
  isOpen: boolean
}

const HandoffRuleForm = () => {
  const [handoffRuleForm, setHandoffRuleForm] =
    useRecoilState<HandoffRuleFormState>(handoffRuleFormAtom)
  const [, setAllHandoffRules] = useRecoilState(allHandoffRulesAtom)
  const { secrets, selectedContact, selectedCompany } = useRecoilValue(
    authenticatedUserAtom,
  )
  const [associationContacts, setAssociationContacts] = useState<APIContact[]>(
    [],
  )
  const editorRef = useRef<EditorRef>(null)
  const [isScheduleValid, setIsScheduleValid] = useState(true)
  const [freshBusinessHours, setFreshBusinessHours] =
    useState<ScheduleData | null>(null)
  const companyBusinessHours =
    'businessHours' in selectedCompany && selectedCompany.businessHours
      ? transformBackendScheduleToUI(selectedCompany.businessHours)
      : getDefaultSchedule()

  // Function to fetch fresh business hours
  const fetchFreshBusinessHours = useCallback(async () => {
    if (!selectedCompany.id) return companyBusinessHours

    try {
      const freshCompany = await findCompanyById(selectedCompany.id)
      if (freshCompany?.businessHours) {
        const fresh = transformBackendScheduleToUI(freshCompany.businessHours)
        setFreshBusinessHours(fresh)
        return fresh
      }
    } catch (error) {
      // console.error('Failed to fetch fresh business hours:', error)
    }
    return companyBusinessHours
  }, [selectedCompany.id, companyBusinessHours])

  // Determine initial schedule and type
  const initialSchedule = handoffRuleForm.handoffRule?.timeRestrictions
    ?.schedule
    ? transformBackendScheduleToUI(
        handoffRuleForm.handoffRule.timeRestrictions.schedule,
      )
    : getDefaultSchedule()
  const initialScheduleType =
    handoffRuleForm.handoffRule && handoffRuleForm.handoffRule.id
      ? getScheduleTypeFromBackendRule(
          handoffRuleForm.handoffRule as APIHandoffRule,
          companyBusinessHours,
        )
      : 'business-hours' // Default new rules to business-hours
  const [scheduleType, setScheduleType] =
    useState<ScheduleType>(initialScheduleType)
  const [localSchedule, setLocalSchedule] = useState(
    getScheduleForDisplay(
      initialScheduleType,
      companyBusinessHours,
      initialSchedule,
    ),
  )

  // // Log initial schedule and scheduleType on mount
  // useEffect(() => {
  //   console.log('[HandoffRuleForm] Initial scheduleType:', scheduleType)
  //   console.log(
  //     '[HandoffRuleForm] Initial schedule:',
  //     getScheduleForDisplay(scheduleType, companyBusinessHours, localSchedule),
  //   )
  // }, [])

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.value = handoffRuleForm.handoffRule?.description || ''
    }
  }, [handoffRuleForm.handoffRule?.description])

  useEffect(() => {
    const fetchContacts = async () => {
      if (!selectedCompany.id) return

      try {
        const contacts = await getContacts(
          secrets.find(s => s.companyId === selectedCompany.id),
          selectedCompany.id,
          selectedCompany.associationIds,
          [
            ContactGroup.Management,
            ContactGroup.Staff,
            ContactGroup.Board,
            ContactGroup.Sponsors,
            ContactGroup.Vendors,
          ],
        )
        setAssociationContacts(contacts)
      } catch (error) {
        toastError('Error fetching contacts')
      }
    }

    fetchContacts()
  }, [selectedCompany, secrets])

  // Fetch fresh business hours when form opens
  useEffect(() => {
    if (handoffRuleForm.isOpen) {
      fetchFreshBusinessHours()
    }
  }, [handoffRuleForm.isOpen, fetchFreshBusinessHours])

  const handleSubmit = async (values: HandoffRuleFormValues) => {
    const timeRestrictions: any = {
      timezone: selectedCompany.timezone,
    }

    if (values.scheduleType === 'business-hours') {
      timeRestrictions.applyDuring = 'business-hours'
      // No schedule needed - backend will use company business hours
    } else if (values.scheduleType === 'after-hours') {
      timeRestrictions.applyDuring = 'after-hours'
      // No schedule needed - backend will compute from company business hours
    } else if (values.scheduleType === 'always') {
      timeRestrictions.applyDuring = 'always'
      // No schedule needed - backend knows this means all day every day
    } else if (values.scheduleType === 'custom') {
      timeRestrictions.applyDuring = 'custom-time-restrictions'
      timeRestrictions.schedule = normalizeBackendSchedule(
        transformScheduleForBackend(localSchedule),
      )
    }

    // Only include id if it exists and is a string
    const updatedHandoffRule: Partial<APIHandoffRule> = {
      ...(handoffRuleForm.handoffRule &&
      typeof handoffRuleForm.handoffRule.id === 'string'
        ? { id: handoffRuleForm.handoffRule.id }
        : {}),
      title: values.title,
      description: values.description,
      associationIds: values.associationIds,
      communicationType: values.communicationType,
      timeRestrictions,
    }

    if (
      handoffRuleForm.handoffRule.id &&
      typeof handoffRuleForm.handoffRule.id === 'string'
    ) {
      updatedHandoffRule.id = handoffRuleForm.handoffRule.id
    }

    if (!selectedContact?.companyId) return

    try {
      if (
        handoffRuleForm.handoffRule.id &&
        typeof handoffRuleForm.handoffRule.id === 'string'
      ) {
        // Update existing handoff rule
        const updatedRule = await updateHandoffRule(
          handoffRuleForm.handoffRule.id,
          selectedContact.companyId,
          updatedHandoffRule,
        )

        setAllHandoffRules(prevRules =>
          prevRules.map(rule =>
            rule.id === handoffRuleForm.handoffRule.id ? updatedRule : rule,
          ),
        )

        toastSuccess('Handoff rule updated')
      } else {
        // Create new handoff rule
        const newRule = await createHandoffRule(
          selectedContact.companyId,
          updatedHandoffRule,
        )

        setAllHandoffRules(prevRules => [...prevRules, newRule])
        toastSuccess('Handoff rule created')
      }

      setHandoffRuleForm({
        handoffRule: {},
        isOpen: false,
      })

      // console.log(
      //   '[HandoffRuleForm] Schedule being sent to backend:',
      //   timeRestrictions.schedule,
      // )
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Error saving handoff rule'
      toastError(errorMessage)
    }
  }

  const initialValues: HandoffRuleFormValues = {
    title: handoffRuleForm.handoffRule?.title || '',
    description: handoffRuleForm.handoffRule?.description || '',
    associationIds: handoffRuleForm.handoffRule?.associationIds || [],
    communicationType: handoffRuleForm.handoffRule?.communicationType || [
      HandoffCommunicationType.Voice,
    ],
    scheduleType: initialScheduleType,
    timeRestrictions: {
      applyDuring:
        (handoffRuleForm.handoffRule?.timeRestrictions as any)?.applyDuring ??
        'business-hours',
      schedule:
        initialScheduleType === 'business-hours'
          ? undefined // No schedule for business-hours - it's just a flag
          : getScheduleForDisplay(
              initialScheduleType,
              companyBusinessHours,
              handoffRuleForm.handoffRule?.timeRestrictions?.schedule
                ? transformBackendScheduleToUI(
                    handoffRuleForm.handoffRule.timeRestrictions.schedule,
                  )
                : getDefaultSchedule(),
            ),
    },
  }

  // console.log('[HandoffRuleForm] companyBusinessHours:', companyBusinessHours)

  return (
    <Formik<HandoffRuleFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange
      validateOnBlur
    >
      {({
        errors,
        touched,
        setFieldValue,
        isSubmitting,
        values,
      }: FormikProps<HandoffRuleFormValues>) => (
        <Form className="bg-white rounded-lg">
          <div className="max-w-[500px] p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">
              {handoffRuleForm.handoffRule.id
                ? 'Edit handoff rule'
                : 'Add/edit handoff'}
            </h2>

            <div className="space-y-1">
              <label
                htmlFor="title"
                className="block text-sm font-semibold text-gray-900"
              >
                Title
              </label>
              <Field
                id="title"
                name="title"
                as={TextInput}
                type="text"
                placeholder="Add a title"
              />
              {errors.title && touched.title && (
                <div className="text-sm text-red-600">{errors.title}</div>
              )}
            </div>

            <div className="space-y-1">
              <label
                htmlFor="associationIds"
                className="block text-sm font-semibold text-gray-900"
              >
                Accounts
              </label>

              <p className="text-[#627088] text-xs font-normal font-['Inter'] leading-[18px]">
                Leave blank if this handoff applies across your company. If it
                is limited to specific properties, select them below.
              </p>

              <PropertySelector
                width="100%"
                value={values.associationIds as string[]}
                onChange={(associationIds: string[]) => {
                  setFieldValue('associationIds', associationIds)
                }}
                allowMultiSelect
                isTaskSheet={false}
                includeCompany={false}
                placeholder="Company-wide"
              />

              {errors.associationIds && touched.associationIds && (
                <div className="text-sm text-red-600">
                  {errors.associationIds}
                </div>
              )}
            </div>
            {/* Schedule Section */}
            <div className="space-y-1">
              <Label className="block text-sm font-semibold text-gray-900">
                Schedule
              </Label>
              {/* Schedule type dropdown for handoff mode */}
              <div className="space-y-1">
                <Select
                  value={scheduleType}
                  onChange={e => {
                    const type = e.target.value as ScheduleType
                    setScheduleType(type)
                    setFieldValue('scheduleType', type)

                    if (type === 'business-hours') {
                      // Business-hours is just a flag - no schedule needed
                      // The backend will use company business hours
                      setFieldValue(
                        'timeRestrictions.applyDuring',
                        'business-hours',
                      )
                      setFieldValue('timeRestrictions.schedule', undefined)
                      setIsScheduleValid(true)
                    } else if (type === 'after-hours') {
                      setFieldValue(
                        'timeRestrictions.applyDuring',
                        'after-hours',
                      )
                      setFieldValue('timeRestrictions.schedule', undefined)
                      setIsScheduleValid(true)
                    } else if (type === 'always') {
                      setFieldValue('timeRestrictions.applyDuring', 'always')
                      setFieldValue('timeRestrictions.schedule', undefined)
                      setIsScheduleValid(true)
                    } else if (type === 'custom') {
                      const newSchedule = getDefaultSchedule()
                      setLocalSchedule(newSchedule)
                      setFieldValue(
                        'timeRestrictions.applyDuring',
                        'custom-time-restrictions',
                      )
                      setFieldValue('timeRestrictions.schedule', newSchedule)
                      setIsScheduleValid(true)
                    }
                    // console.log('[HandoffRuleForm] Dropdown changed to:', type)
                    // console.log(
                    //   '[HandoffRuleForm] Schedule after dropdown change:',
                    //   newSchedule,
                    // )
                  }}
                  style={{
                    width: '100%',
                    height: 40,
                    borderRadius: '4px',
                    border: '1px solid #E0E0E0',
                    padding: '0 12px',
                    fontSize: 14,
                  }}
                >
                  <option value="business-hours">Business hours</option>
                  <option value="after-hours">After hours</option>
                  <option value="always">Always</option>
                  <option value="custom">Custom schedule</option>
                </Select>
              </div>
              {/* Show the scheduling UI only for custom, otherwise show summary or nothing */}
              {scheduleType === 'custom' && (
                <ScheduleField
                  value={localSchedule}
                  onChange={setLocalSchedule}
                  errors={errors}
                  touched={touched}
                  onValidationChange={setIsScheduleValid}
                />
              )}
              {/* Warning when all days are disabled for custom schedule */}
              {scheduleType === 'custom' &&
                localSchedule.days.every(day => !day.enabled) && (
                  <div
                    className="text-sm text-amber-600"
                    style={{ marginTop: 8 }}
                  >
                    ⚠️ This rule will never be active because no days are
                    enabled.
                  </div>
                )}
              {scheduleType !== 'custom' && (
                <div style={{ margin: '12px 0', color: '#627088' }}>
                  {scheduleType === 'business-hours' &&
                    (getScheduleForDisplay(
                      'business-hours',
                      companyBusinessHours,
                      localSchedule,
                      freshBusinessHours || undefined,
                    ).days.every(day => !day.enabled) ? (
                      <span style={{ color: '#F59E0B' }}>
                        ⚠️ No business hours set - rule will never be active
                      </span>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {scheduleSummary(
                          getScheduleForDisplay(
                            'business-hours',
                            companyBusinessHours,
                            localSchedule,
                            freshBusinessHours || undefined,
                          ),
                        )}
                      </span>
                    ))}
                  {scheduleType === 'after-hours' &&
                    (getScheduleForDisplay(
                      'after-hours',
                      companyBusinessHours,
                      localSchedule,
                      freshBusinessHours || undefined,
                    ).days.every(day => !day.enabled) ? (
                      <span style={{ color: '#F59E0B' }}>
                        ⚠️ No business hours set - rule will never be active
                      </span>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {scheduleSummary(
                          getScheduleForDisplay(
                            'after-hours',
                            companyBusinessHours,
                            localSchedule,
                            freshBusinessHours || undefined,
                          ),
                        )}
                      </span>
                    ))}
                  {scheduleType === 'always' && 'This rule is always active.'}
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label>Communication type</Label>
              <FlexRow align="center">
                {values.communicationType?.includes(
                  HandoffCommunicationType.Voice,
                ) ? (
                  <MdCheckBox
                    onClick={() => {
                      const newType = !values.communicationType
                        ? [HandoffCommunicationType.Voice]
                        : values.communicationType?.includes(
                            HandoffCommunicationType.Voice,
                          )
                        ? values.communicationType?.includes(
                            HandoffCommunicationType.Text,
                          )
                          ? [HandoffCommunicationType.Text]
                          : []
                        : [
                            ...values.communicationType,
                            HandoffCommunicationType.Voice,
                          ]

                      setFieldValue('communicationType', newType)
                    }}
                    style={{ fontSize: 18, marginRight: 5 }}
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    onClick={() => {
                      const newType = !values.communicationType
                        ? [HandoffCommunicationType.Voice]
                        : values.communicationType?.includes(
                            HandoffCommunicationType.Voice,
                          )
                        ? values.communicationType?.includes(
                            HandoffCommunicationType.Text,
                          )
                          ? [HandoffCommunicationType.Text]
                          : []
                        : [
                            ...values.communicationType,
                            HandoffCommunicationType.Voice,
                          ]

                      setFieldValue('communicationType', newType)
                    }}
                    style={{
                      fontSize: 18,
                      color: '#C9CED6',
                      marginRight: 5,
                    }}
                  />
                )}
                <p>Call</p>
              </FlexRow>
              <FlexRow align="center">
                {values.communicationType?.includes(
                  HandoffCommunicationType.Text,
                ) ? (
                  <MdCheckBox
                    onClick={() => {
                      const newType = !values.communicationType
                        ? [HandoffCommunicationType.Text]
                        : values.communicationType?.includes(
                            HandoffCommunicationType.Text,
                          )
                        ? values.communicationType?.includes(
                            HandoffCommunicationType.Voice,
                          )
                          ? [HandoffCommunicationType.Voice]
                          : []
                        : [
                            ...values.communicationType,
                            HandoffCommunicationType.Text,
                          ]

                      setFieldValue('communicationType', newType)
                    }}
                    style={{ fontSize: 18, marginRight: 5 }}
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    onClick={() => {
                      const newType = !values.communicationType
                        ? [HandoffCommunicationType.Text]
                        : values.communicationType?.includes(
                            HandoffCommunicationType.Text,
                          )
                        ? values.communicationType?.includes(
                            HandoffCommunicationType.Voice,
                          )
                          ? [HandoffCommunicationType.Voice]
                          : []
                        : [
                            ...values.communicationType,
                            HandoffCommunicationType.Text,
                          ]

                      setFieldValue('communicationType', newType)
                    }}
                    style={{
                      fontSize: 18,
                      color: '#C9CED6',
                      marginRight: 5,
                    }}
                  />
                )}
                <p>SMS or email</p>
              </FlexRow>
              {errors.communicationType && touched.communicationType && (
                <div className="text-sm text-red-600">
                  {errors.communicationType}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <label
                htmlFor="description"
                className="block text-sm font-semibold text-gray-900"
              >
                Prompt
              </label>
              <p className="text-[#627088] text-xs font-normal font-['Inter'] leading-[18px] pb-2">
                Actions the agent can take on tasks include: assigning the task,
                adding subscribers, updating the workspace, adding categories,
                adding a due date, changing the task status, and sending an
                internal comment.
              </p>

              <p className="text-[#627088] text-xs font-normal font-['Inter'] leading-[18px] pb-2">
                During phone calls, your agent can take a message or transfer
                calls according to a phone tree. To trigger a phone tree,
                specify the phone numbers to dial; they will be dialed in the
                order they are written. If call cannot be connected to a
                recipient via phone tree, a message will be recorded.
              </p>

              <div
                style={{
                  border: '1px solid #E0E0E0',
                  borderRadius: '4px',
                  padding: '0 10px',
                  minHeight: '200px',
                }}
              >
                <Editor
                  ref={editorRef}
                  placeholder="Specify your handoff rules"
                  associationId={undefined}
                  contacts={associationContacts}
                  onChange={val => {
                    setFieldValue('description', val)
                  }}
                  forceGroups
                />
              </div>
              {errors.description && touched.description && (
                <div className="text-sm text-red-600">{errors.description}</div>
              )}
            </div>
          </div>

          <FixedBottomButtons>
            <FlexRow style={{ gap: 10 }} justify="flex-end">
              <PrimaryButton
                type="button"
                light
                onClick={() => {
                  setHandoffRuleForm({
                    handoffRule: {},
                    isOpen: false,
                  })
                }}
              >
                Cancel
              </PrimaryButton>
              <PrimaryButton
                type="submit"
                disabled={
                  isSubmitting ||
                  Object.keys(errors).length > 0 ||
                  (scheduleType === 'custom' && !isScheduleValid)
                }
              >
                {handoffRuleForm.handoffRule.id ? 'Update' : 'Next'}
              </PrimaryButton>
            </FlexRow>
          </FixedBottomButtons>
        </Form>
      )}
    </Formik>
  )
}

export default HandoffRuleForm
